import { useDataSource, type Entity } from "@firecms/core";
import { useCallback, useEffect, useState } from "react";
import {
  buildSpacesCollection,
  type Space,
} from "../collections/spaces";
import {
  getAccountPath,
  type AccountCollectionId,
} from "../workspace/collectionIds";

const spacesCache = new Map<string, Entity<Space>[]>();

export const useSpaces = (account?: AccountCollectionId) => {
  const { fetchCollection } = useDataSource();
  const accountId = account?.accountId;

  const [spaces, setSpaces] = useState<Entity<Space>[] | undefined>(() =>
    accountId ? spacesCache.get(accountId) : undefined
  );
  const [loading, setLoading] = useState(
    () => !!(accountId && !spacesCache.has(accountId))
  );

  const refetch = useCallback(async () => {
    if (!accountId) {
      setSpaces(undefined);
      setLoading(false);
      return;
    }

    const hasCache = spacesCache.has(accountId);
    if (!hasCache) {
      setLoading(true);
    }

    const collection = buildSpacesCollection({ accountId });
    const path = `${getAccountPath({ accountId })}/spaces`;
    const result = await fetchCollection<Space>({
      path,
      collection,
    });
    spacesCache.set(accountId, result);
    setSpaces(result);
    setLoading(false);
  }, [accountId, fetchCollection]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { spaces, loading, refetch };
};
